import React, { useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { IconButton, InputAdornment, Tooltip } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import Close from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { DocumentTemplateType } from '../../../api-client/autogenerated';
import { ProjectFilePathLink } from '../types';
import { documentTypeToUrl } from '../../../scripts/utils';
import { getProjectState } from '../../../features/project/selectors';
import { getFilesystemFileViewLink } from '../../../models/api/filesystem';
import { addSnackbar } from '../../../features/snackbar/actions';
import FormTextField from './FormTextField';
import BrowseCenterlineFieldDialog, {
  ProjectFilePathSelection,
} from './BrowseCenterlineFieldDialog';

const useStyles = makeStyles(() => ({
  // Reads as the field's value rather than as a control: same border and height as the text field
  // beside it, with the name itself as the link.
  linkBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minHeight: 40,
    padding: '4px 4px 4px 12px',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 4,
    // Without this the document's name sets the cell's min-content width and stretches this
    // column of the phase grid, squeezing the three beside it. The name ellipsizes instead.
    minWidth: 0,
  },
  linkText: {
    flexGrow: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconButton: {
    padding: 4,
  },
  // The outlined input reserves 14px to the right of its adornment; the button carries its own
  // padding, so that much again leaves the icon floating well short of the border.
  adornedEnd: {
    paddingRight: 2,
  },
}));

/**
 * One of the Quality Control Form's Project File Paths cells.
 *
 * Typed text by default. The link button picks a CENTERLINE document or a CENTERLINE Drive file
 * instead, and the cell then shows that thing's name as a link, opening in a new tab either way --
 * the document's page, or the file itself. Clearing empties the cell, link and name together.
 */
export default function ProjectFilePathField({
  name,
  control,
  label,
  InputLabelProps,
  disabled,
}: {
  name: string;
  control: Control<any>;
  label?: string;
  InputLabelProps?: Record<string, unknown>;
  disabled?: boolean;
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const project = useSelector(getProjectState);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isOpeningFile, setIsOpeningFile] = useState(false);

  const valueField = useController({ name, control });
  // All sixteen cells' link targets live in one map on the form, keyed by field name, so the form
  // keeps every existing path field as the plain string it has always been.
  const linksField = useController({ name: 'projectFilePathLinks', control });

  const links = (linksField.field.value ?? {}) as Record<string, ProjectFilePathLink>;
  const link = links[name];
  const isDisabled = disabled || valueField.field.disabled;

  const setLink = (next: ProjectFilePathLink | undefined) => {
    const { [name]: _removed, ...rest } = links;
    linksField.field.onChange(next ? { ...rest, [name]: next } : rest);
  };

  /** Empties the cell: the link and the name it was showing both go. */
  const clearPath = () => {
    setLink(undefined);
    valueField.field.onChange('');
  };

  const handleSelect = (selection: ProjectFilePathSelection) => {
    valueField.field.onChange(selection.label);
    setLink({
      documentId: selection.documentId,
      documentType: selection.documentType,
      fileNodeKey: selection.fileNodeKey,
      fileNodeOwnerId: selection.fileNodeOwnerId,
    });
  };

  const openDriveFile = async () => {
    if (!project?.id || !link?.fileNodeKey || isOpeningFile) return;
    // Opened from the link the API hands back, which expires, so it is fetched per click rather
    // than held on the form.
    setIsOpeningFile(true);
    try {
      const viewLink = await getFilesystemFileViewLink(
        project.id,
        link.fileNodeKey,
        link.fileNodeOwnerId ?? '',
      );
      if (viewLink) window.open(viewLink, '_blank', 'noopener,noreferrer');
      else
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: 'That file could not be opened',
            severity: 'error',
          }),
        );
    } finally {
      setIsOpeningFile(false);
    }
  };

  const linkButton = (
    <Tooltip title="Link to a CENTERLINE document or Drive file">
      <IconButton
        className={classes.iconButton}
        disabled={isDisabled}
        onClick={() => setDialogOpen(true)}
      >
        <LinkIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const documentPath =
    link?.documentId && link.documentType
      ? `/main/projects/${project?.id}/documents/${
          documentTypeToUrl[link.documentType as DocumentTemplateType]
        }/${link.documentId}${link.documentType === DocumentTemplateType.PunchList ? '/edit' : ''}`
      : undefined;

  return (
    <>
      {link ? (
        <div className={classes.linkBox}>
          {/* The name is ellipsized to its column, so hovering has to give it back in full. */}
          <Tooltip title={valueField.field.value || ''}>
            {documentPath ? (
              // A new tab, not a react-router push: this form blocks navigation while it has
              // unsaved edits, and following a path in place would strand them. An anchor rather
              // than a Link so middle-click and cmd-click behave as they look.
              <a
                href={documentPath}
                className={classes.linkText}
                target="_blank"
                rel="noopener noreferrer"
              >
                {valueField.field.value}
              </a>
            ) : (
              // The file is not a page in the app; it opens from a short-lived URL fetched on
              // click, which is why this one has no href to follow.
              <a
                href="#"
                className={classes.linkText}
                onClick={(event) => {
                  event.preventDefault();
                  void openDriveFile();
                }}
              >
                {valueField.field.value}
              </a>
            )}
          </Tooltip>
          <Tooltip title="Clear this path">
            <IconButton className={classes.iconButton} disabled={isDisabled} onClick={clearPath}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ) : (
        <FormTextField
          name={name}
          control={control}
          label={label}
          InputLabelProps={InputLabelProps}
          InputProps={{
            classes: { adornedEnd: classes.adornedEnd },
            endAdornment: <InputAdornment position="end">{linkButton}</InputAdornment>,
          }}
        />
      )}

      <BrowseCenterlineFieldDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        onSelect={handleSelect}
      />
    </>
  );
}
